import { allWorks, formatViews, getWorkById, relatedWorks, workFormat } from "@/mocks";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";
import { newsList } from "@/mocks/news";

export default function Detail() {
  const { id } = useParams();
  const work = getWorkById(id);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setCopied(false);
    window.scrollTo({ top: 0 });
  }, [id]);

  if (!work) {
    return (
      <EmptyState
        title="影视作品不存在"
        description="你访问的影视作品可能已下线或链接有误，请返回首页重新浏览其他影视内容。"
        icon="ri-error-warning-line"
      />
    );
  }

  const related = relatedWorks(work, 4);
  const ownArticles = newsList.filter((n) => n.relatedWorkId === work.id);
  const fallbackArticles = newsList.filter((n) => n.relatedWorkId !== work.id).slice(0, 2);
  const displayArticles = ownArticles.length ? ownArticles : fallbackArticles;

  const copyLink = async () => {
    const url = `${window.location.origin}${__BASE_PATH__.replace(/\/$/, "")}/detail/${work.id}`;
    try {
      await navigator.clipboard.writeText(url);
    } catch {
      const textarea = document.createElement("textarea");
      textarea.value = url;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2400);
  };

  const infoRows = [
    { label: "导演", value: work.director },
    { label: "主演", value: work.cast.join(" / ") },
    { label: "类型", value: work.genres.join(" · ") },
    { label: "地区", value: work.region },
    { label: "年份", value: String(work.year) },
    { label: "片长", value: work.duration },
    { label: "播放状态", value: work.status },
    { label: "浏览量", value: `${formatViews(work.views)} 次` },
  ];

  return (
    <SiteLayout>
      <div className="mx-auto w-full max-w-content px-4 pt-4 md:px-6 md:pt-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: work.type === "movie" ? "电影分类" : "电视剧分类", to: `/category?type=${work.type}` },
            { label: work.title },
          ]}
        />
      </div>

      <section className="w-full bg-background-50 py-5 md:py-8">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-6 px-4 md:px-6 lg:grid-cols-12 lg:gap-8">
          <div className="lg:col-span-4">
            <div className="relative aspect-[3/4] w-full max-w-[280px] overflow-hidden rounded-lg bg-background-200 lg:max-w-none">
              <img
                src={work.poster}
                alt={`${work.title} 海报`}
                title={`${work.title} 剧情介绍`}
                className="h-full w-full object-cover object-top"
              />
              <span className="absolute left-3 top-3 rounded bg-accent-500 px-2 py-1 text-[11px] font-bold text-background-50">
                {work.rating.toFixed(1)} 分
              </span>
            </div>
          </div>

          <div className="lg:col-span-8">
            <p className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="rounded bg-primary-50 px-2 py-0.5 font-medium text-primary-700">{workFormat(work.type)}</span>
              <span className="whitespace-nowrap">{work.year}</span>
              <span className="text-background-400">·</span>
              <span className="whitespace-nowrap">{work.region}</span>
              <span className="text-background-400">·</span>
              <span className="whitespace-nowrap">{work.genres.join(" / ")}</span>
            </p>
            <h1 className="mt-3 font-heading text-2xl font-bold leading-tight text-foreground-950 md:text-[32px]">
              {work.title}
            </h1>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600 md:text-[15px]">{work.summary}</p>

            <div className="mt-4 flex flex-wrap gap-1.5">
              {work.tags.map((tag) => (
                <span key={tag} className="rounded-full bg-secondary-100 px-2.5 py-1 text-[11px] text-secondary-900">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-5 grid grid-cols-1 gap-x-6 gap-y-0 rounded-lg border border-background-200/80 bg-background-100 px-4 py-1 sm:grid-cols-2">
              {infoRows.map((row) => (
                <div key={row.label} className="flex items-start gap-3 border-b border-background-200/70 py-2.5">
                  <span className="w-14 shrink-0 text-xs text-foreground-500">{row.label}</span>
                  <span className="min-w-0 text-xs leading-relaxed text-foreground-900">{row.value}</span>
                </div>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={copyLink}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-link text-base leading-none" />
                </span>
                复制分享链接
              </button>
              <Link
                to={`/category?type=${work.type}`}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-left-line text-base leading-none" />
                </span>
                返回分类
              </Link>
              <span className="text-[11px] text-foreground-500">演示功能：链接仅在本地复制</span>
            </div>
            {copied && (
              <p className="animate-fade-in mt-3 inline-flex items-center gap-1.5 rounded-md bg-accent-100 px-3 py-2 text-xs font-medium text-accent-900">
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-check-double-line text-base leading-none" />
                </span>
                已复制影视详情页链接，可粘贴分享给好友
              </p>
            )}
          </div>
        </div>
      </section>

      <section className="w-full border-t border-background-200/70 bg-background-50 py-8 md:py-10">
        <div className="mx-auto grid w-full max-w-content grid-cols-1 gap-8 px-4 md:px-6 lg:grid-cols-12 lg:gap-8">
          <article className="lg:col-span-8">
            <div className="flex items-center gap-2">
              <span className="h-4 w-1 rounded-full bg-accent-500" />
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">剧情简介</h2>
            </div>
            <div className="mt-4 space-y-4">
              {work.synopsis.map((paragraph, i) => (
                <p key={i} className="text-sm leading-[1.9] text-foreground-700 md:text-[15px]">
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-6 rounded-lg border border-background-200/80 bg-secondary-50 p-4 md:p-5">
              <div className="flex items-center gap-2">
                <span className="flex h-6 w-6 items-center justify-center text-secondary-600">
                  <i className="ri-lightbulb-line text-lg leading-none" />
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-950">观看提示</h3>
              </div>
              <p className="mt-2 text-xs leading-relaxed text-foreground-700 md:text-sm">
                本页仅提供影视作品的剧情介绍与资料查阅，不提供播放或下载。相关剧情文字由编辑整理，为演示数据，实际内容以官方发布为准。
              </p>
            </div>
          </article>

          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200/80 bg-background-100 p-4 md:p-5">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-base font-bold text-foreground-950">相关资讯</h2>
                <Link to="/news" className="cursor-pointer whitespace-nowrap text-xs text-primary-600 hover:text-primary-700">
                  更多
                </Link>
              </div>
              <div className="mt-1">
                {displayArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/news/detail/${article.id}`}
                    className="group flex cursor-pointer items-start gap-2.5 border-b border-background-200/70 py-3 last:border-b-0"
                  >
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded bg-background-50 text-accent-700">
                      <i className="ri-news-line text-xs leading-none" />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 block text-xs leading-relaxed text-foreground-900 transition-colors group-hover:text-primary-700">
                        {article.title}
                      </span>
                      <span className="mt-1 block text-[11px] text-foreground-500">{article.date}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-primary-500 p-5 text-background-50">
              <h2 className="font-heading text-sm font-bold">同类影视推荐</h2>
              <p className="mt-1.5 text-xs leading-relaxed text-background-100/85">
                根据类型与标签为你关联了 {related.length} 部相似作品。
              </p>
              <div className="mt-3 space-y-2">
                {related.slice(0, 3).map((item) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="flex cursor-pointer items-center justify-between gap-2 rounded-md bg-background-50/10 px-3 py-2 text-xs text-background-50 transition-colors hover:bg-background-50/20"
                  >
                    <span className="line-clamp-1">{item.title}</span>
                    <i className="ri-arrow-right-line shrink-0 text-sm leading-none" />
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="w-full border-t border-background-200/70 bg-background-100 py-8 md:py-12">
        <div className="mx-auto w-full max-w-content px-4 md:px-6">
          <div className="flex items-end justify-between gap-4">
            <div>
              <p className="font-label text-[11px] font-semibold uppercase tracking-[0.2em] text-accent-600">Related</p>
              <h2 className="mt-1.5 font-heading text-lg font-bold text-foreground-950 md:text-xl">相关推荐影视</h2>
            </div>
            <Link
              to={`/category?type=${work.type}`}
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs font-medium text-primary-600 hover:text-primary-700 md:text-sm"
            >
              更多同类
              <i className="ri-arrow-right-line text-sm leading-none" />
            </Link>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
            {related.map((item) => (
              <WorkCard key={item.id} work={item} />
            ))}
          </div>
        </div>
      </section>

      <div className="mx-auto w-full max-w-content px-4 py-6 md:px-6">
        <p className="text-[11px] leading-relaxed text-foreground-500">
          本站共收录 {allWorks.length} 部影视作品资料，本页相关资讯 {displayArticles.length} 篇。影视资料均为演示数据，仅供信息查阅。
        </p>
      </div>
    </SiteLayout>
  );
}